import React, { useMemo, useState } from 'react'
import { useCrmInfo } from '../../components/CrmLayout/CrmLayout.context'
import { ITableState } from '@utils/sort'
import { initialPagination } from '../../../MainPage/MainPage'
import moment from 'moment'
import useCrmInfoApi from '@api/useCrmInfoApi'
import { graphCrmConfig } from '../../graph.config'
import { useGetUserList } from '@api/tasksCrmApi'
import { useRedirect } from '@hooks/useRedirect'
import styles from './CrmStatPage.module.scss'
import WithLabel from '@components/ui/WithLabel/WithLabel'
import IntervalSelect from '@components/ui/IntervalSelect/IntervalSelect'
import { BaseDefaultStatusesColumn, statusesListD } from '@enums/base'
import { statusMapper } from '@utils/mappers'
import { Line } from '@ant-design/plots'
import CustomTable from '@components/ui/CustomTable/CustomTable'
import { crmTableColumns } from '../../crmTable.column'
import PageSpinner from '@components/ui/PageSpinner/PageSpinner'
import { IconCalendar, IconPhoneCall, IconPresentationPoll } from '@utils/icons'
import Box from '@components/ui/Box/Box'

const CrmStatPage: React.FC = () => {
  const { userRedirect } = useRedirect()
  const [crmInfo] = useCrmInfo()
  const [state, setState] = useState<ITableState>(initialPagination)
  const [selectedDate, setSelectedDate] = useState<[moment.Moment | null, moment.Moment | null]>([
    moment().startOf('week'),
    moment().endOf('week'),
  ])

  const { result, isLoading } = useCrmInfoApi({
    crmId: crmInfo?.id,
    dates: selectedDate,
  })

  const config = useMemo(() => {
    return !result ? null : graphCrmConfig(result.crmCall)
  }, [result])

  const getInterval = useMemo(
    () => [selectedDate[0].format(), selectedDate[1].format()],
    [selectedDate],
  )

  const crmIdMemo = useMemo(() => crmInfo?.id, [crmInfo?.id])

  const { rows: currentTable, count } = useGetUserList({
    dates: getInterval,
    id: crmIdMemo,
    sort: state.sort,
    pagination: state.pagination,
  })

  const mappedTableData = useMemo(() => {
    return currentTable
      ? currentTable.map((el) => ({
          ...el,
          key: el.userid,
          nick_name: (
            <span className={styles.tableLink} onClick={() => userRedirect(el.userid)}>
              {el.nick_name}
            </span>
          ),
          login: (
            <span className={styles.tableLink} onClick={() => userRedirect(el.userid)}>
              {el.login}
            </span>
          ),
        }))
      : undefined
  }, [currentTable])

  return (
    <Box className={styles.container}>
      {result && !isLoading ? (
        <>
          <div className={styles.wrapper}>
            <div className={styles.filters}>
              <WithLabel
                label={'Интервал'}
                icon={<IconCalendar width='18' height='18' fill='var(--color-main)' />}
              >
                <IntervalSelect
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  showDays={false}
                  showMonth={true}
                />
              </WithLabel>
            </div>
            <div className={styles.graph}>
              {result.crmCall.length > 1 ? (
                <Line {...config} />
              ) : (
                <div className={styles.emptyGraph}>
                  <IconPresentationPoll width='64' height='64' fill='var(--color-main)' />
                  <p>Нет данных за этот период</p>
                </div>
              )}
            </div>
          </div>

          {result.crmCall.length > 1 && (
            <div className={styles.crmInfoCard}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>
                  <IconPhoneCall width='18' height='18' fill='var(--color-font)' />
                  Всего за период:
                </div>
                <div className={styles.list}>
                  {statusesListD.map((el) => (
                    <div key={el} className={styles.listRow}>
                      <div className={styles.title}>
                        {statusMapper(el as BaseDefaultStatusesColumn)}
                      </div>
                      <span className={styles.dots} />
                      <div> {result.sumCall[el]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={styles.userTable}>
            <CustomTable
              columns={crmTableColumns}
              dataSource={mappedTableData}
              allCount={count}
              updatePagination={setState}
              pagination={state.pagination}
            />
          </div>
        </>
      ) : (
        <PageSpinner />
      )}
    </Box>
  )
}

export default CrmStatPage
